import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, RotateCcw, Timer, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { speak, cancelSpeech } from "@/lib/tts";
import { ttsLangFor } from "@/lib/i18n";
import { fireFirstWordConfetti } from "@/lib/celebrate";

/**
 * First-Then visual schedule. A two-step sequence used in autism education:
 * "First [task], then [reward/preferred activity]". This is a calming,
 * clinically-supported tool for transitions and boundary-setting.
 *
 * Flow: pick `First` tile -> pick `Then` tile -> tap First when started ->
 * tap First again when done (it gets a check) -> Then is now active. Tap
 * Then when done = celebration.
 */

function TilePicker({ open, title, tiles, onPick, onClose }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" data-testid="ft-tile-picker">
      <div className="brutal-card p-5 w-full max-w-3xl max-h-[88vh] overflow-y-auto tile-scroll bg-white">
        <div className="flex items-center justify-between mb-3">
          <h2 className="display text-xl font-bold">{title}</h2>
          <button type="button" className="pill-btn" onClick={onClose} data-testid="ft-picker-close">
            <X size={16} />
          </button>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {tiles.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onPick(t)}
              className="tile-btn tile-md"
              style={{ background: t.color || "#FFFFFF" }}
              data-testid={`ft-pick-${t.id}`}
            >
              {t.custom_image_b64 ? (
                <img src={t.custom_image_b64} alt="" style={{ height: "60%", width: "auto", maxWidth: "85%" }} className="object-cover rounded-xl" />
              ) : (
                <span className="tile-emoji" aria-hidden>{t.emoji || "🔤"}</span>
              )}
              <span className="tile-label">{t.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function BigTile({ tile, label, status, onClick, "data-testid": testId }) {
  // status: "pending" | "active" | "done"
  const ring =
    status === "active"
      ? "ring-8 ring-[color:var(--coral)]"
      : status === "done"
      ? "opacity-60"
      : "ring-4 ring-[color:var(--ink-soft)]";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative brutal-card p-6 flex flex-col items-center justify-center min-h-[280px] sm:min-h-[360px] transition-transform hover:translate-y-[-2px] ${ring}`}
      style={{ background: tile?.color || "#FBEFD9" }}
      data-testid={testId}
    >
      <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--ink-soft)] mb-3">{label}</div>
      {tile ? (
        <>
          {tile.custom_image_b64 ? (
            <img src={tile.custom_image_b64} alt="" className="object-cover rounded-2xl" style={{ height: 140, width: "auto" }} />
          ) : (
            <div className="text-7xl mb-3" aria-hidden>{tile.emoji || "🔤"}</div>
          )}
          <div className="display text-2xl sm:text-3xl font-extrabold text-center">{tile.label}</div>
        </>
      ) : (
        <div className="display text-2xl text-[color:var(--ink-soft)]">Tap to choose</div>
      )}
      {status === "done" && (
        <span className="absolute -top-3 -right-3 bg-[color:var(--gold)] border-[3px] border-[color:var(--ink)] rounded-full w-14 h-14 flex items-center justify-center shadow-[3px_3px_0_0_var(--ink)]">
          <CheckCircle2 size={28} />
        </span>
      )}
    </button>
  );
}

export default function FirstThen() {
  const { profileId } = useParams();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [tiles, setTiles] = useState([]);
  const [first, setFirst] = useState(null);
  const [then, setThen] = useState(null);
  const [firstDone, setFirstDone] = useState(false);
  const [thenDone, setThenDone] = useState(false);
  const [picker, setPicker] = useState(null); // 'first' | 'then' | null
  const [error, setError] = useState("");

  // Timer
  const [timerSec, setTimerSec] = useState(0);
  const [timerLeft, setTimerLeft] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: ts }] = await Promise.all([
          api.get(`/profiles/${profileId}`),
          api.get(`/profiles/${profileId}/tiles`),
        ]);
        setProfile(p);
        setTiles(ts);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
    return () => {
      cancelSpeech();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [profileId]);

  const lang = profile?.language || "en";
  const ttsLang = ttsLangFor(lang);
  const sortedTiles = useMemo(
    () => [...tiles].sort((a, b) => (a.label || "").localeCompare(b.label || "")),
    [tiles]
  );

  const sayTile = (tile) => {
    if (!tile) return;
    cancelSpeech();
    speak(tile.speak_text || tile.label, {
      rate: profile?.voice_rate ?? 0.95,
      pitch: profile?.voice_pitch ?? 1.05,
      voiceName: profile?.voice_name,
      lang: ttsLang,
    });
  };

  const onPickFirst = (tile) => {
    setFirst(tile);
    setFirstDone(false);
    setPicker(null);
    sayTile(tile);
  };
  const onPickThen = (tile) => {
    setThen(tile);
    setThenDone(false);
    setPicker(null);
    sayTile(tile);
  };

  const tapFirst = () => {
    if (!first) { setPicker("first"); return; }
    if (firstDone) return; // already done — wait for then
    sayTile(first);
    // Mark first as done after a short delay so the kid sees the speak first.
    setTimeout(() => setFirstDone(true), 350);
  };

  const tapThen = () => {
    if (!then) { setPicker("then"); return; }
    if (!firstDone) {
      // Don't let kids skip "First" — gentle nudge.
      sayTile({ ...first, speak_text: "First!" });
      return;
    }
    if (thenDone) return;
    sayTile(then);
    setTimeout(() => {
      setThenDone(true);
      fireFirstWordConfetti();
      const yay = lang === "es" ? "¡Lo hiciste!" : lang === "fr" ? "Bravo !" : "You did it!";
      speak(yay, {
        rate: 1.0, pitch: 1.15, voiceName: profile?.voice_name, lang: ttsLang,
      });
    }, 350);
  };

  const reset = () => {
    setFirstDone(false);
    setThenDone(false);
    cancelTimer();
  };

  const startTimer = (seconds) => {
    cancelTimer();
    setTimerSec(seconds);
    setTimerLeft(seconds);
    timerRef.current = setInterval(() => {
      setTimerLeft((l) => {
        if (l <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          // Audible cue
          const done = lang === "es" ? "El tiempo terminó" : lang === "fr" ? "Le temps est écoulé" : "Time's up";
          speak(done, { rate: 1.0, pitch: 1.1, voiceName: profile?.voice_name, lang: ttsLang });
          return 0;
        }
        return l - 1;
      });
    }, 1000);
  };

  const cancelTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    setTimerSec(0);
    setTimerLeft(0);
  };

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="ft-loading">
        <div className="display text-2xl">{error || "Loading…"}</div>
      </div>
    );
  }

  const firstStatus = firstDone ? "done" : !first ? "pending" : "active";
  const thenStatus = thenDone ? "done" : !then ? "pending" : firstDone ? "active" : "pending";
  const timerPct = timerSec > 0 ? (timerLeft / timerSec) * 100 : 0;

  return (
    <div className="min-h-screen px-4 sm:px-8 py-6">
      <div className="max-w-5xl mx-auto">
        {/* Top bar */}
        <header className="flex items-center gap-3 mb-5">
          <Link to={`/board/${profileId}`} className="pill-btn" data-testid="ft-back">
            <ArrowLeft size={16} />
          </Link>
          <h1 className="display text-2xl sm:text-3xl font-extrabold">First, then…</h1>
          <div className="flex-1" />
          <button
            type="button"
            className="pill-btn"
            onClick={reset}
            data-testid="ft-reset"
            aria-label="Reset"
          >
            <RotateCcw size={16} />
            <span className="hidden sm:inline">Reset</span>
          </button>
        </header>

        {/* Optional countdown timer banner */}
        {timerSec > 0 && (
          <div className="brutal-card bg-white p-3 mb-5" data-testid="ft-timer">
            <div className="flex items-center gap-3 mb-2">
              <Timer size={18} className="text-[color:var(--coral)]" />
              <span className="display font-bold flex-1">
                {Math.floor(timerLeft / 60)}:{String(timerLeft % 60).padStart(2, "0")} left
              </span>
              <button type="button" className="pill-btn" onClick={cancelTimer} data-testid="ft-timer-cancel">
                <X size={14} /> Stop
              </button>
            </div>
            <div className="h-3 w-full rounded-full border-[3px] border-[color:var(--ink)] bg-white overflow-hidden">
              <div
                className="h-full bg-[color:var(--coral)] transition-all duration-1000 linear"
                style={{ width: `${timerPct}%` }}
              />
            </div>
          </div>
        )}

        {/* Pair */}
        <section className="grid grid-cols-1 sm:grid-cols-[1fr,auto,1fr] gap-4 sm:gap-6 items-stretch">
          <BigTile
            tile={first}
            label="First"
            status={firstStatus}
            onClick={tapFirst}
            data-testid="ft-first"
          />
          <div className="hidden sm:flex items-center justify-center">
            <ArrowRight size={64} strokeWidth={3} className="text-[color:var(--ink)]" />
          </div>
          <div className="flex sm:hidden items-center justify-center my-1" aria-hidden>
            <ArrowRight size={36} className="rotate-90 text-[color:var(--ink)]" />
          </div>
          <BigTile
            tile={then}
            label="Then"
            status={thenStatus}
            onClick={tapThen}
            data-testid="ft-then"
          />
        </section>

        {/* Hints + change buttons */}
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <button type="button" className="pill-btn" onClick={() => setPicker("first")} data-testid="ft-change-first">
            Change "First"
          </button>
          <button type="button" className="pill-btn" onClick={() => setPicker("then")} data-testid="ft-change-then">
            Change "Then"
          </button>
          <div className="flex-1" />
          <span className="text-[color:var(--ink-soft)] text-sm">Quick timer:</span>
          {[2, 5, 10].map((m) => (
            <button
              key={m}
              type="button"
              className="pill-btn gold"
              onClick={() => startTimer(m * 60)}
              data-testid={`ft-timer-${m}`}
            >
              {m}m
            </button>
          ))}
        </div>

        {!first || !then ? (
          <div className="brutal-card bg-white p-4 mt-5 text-[color:var(--ink-soft)]">
            Tap each box to choose a tile from {profile.name}'s board. "First" is the task or expectation. "Then" is the reward
            or preferred activity. Tap "First" again when done — that unlocks "Then".
          </div>
        ) : null}

        <TilePicker
          open={picker === "first"}
          title='Pick the "First" tile'
          tiles={sortedTiles}
          onPick={onPickFirst}
          onClose={() => setPicker(null)}
        />
        <TilePicker
          open={picker === "then"}
          title='Pick the "Then" tile'
          tiles={sortedTiles}
          onPick={onPickThen}
          onClose={() => setPicker(null)}
        />
      </div>
    </div>
  );
}
